import { Alert, Link } from "@mui/material";
import * as React from "react";

import { basename } from "@/shared/domain/files/filesystem";
import { isDocumentViewable } from "@/shared/domain/files/fileTypes";
import { actions, useVizDispatch } from "@/shared/state/visualization";

import {
  isTabbedSidebarView,
  SidebarTab,
  useWorkspaceSidebar,
} from "../sidebarContext";
import { Topics } from "../Topics";
import {
  useEphemeralWorkspaceState,
  useWorkspaceTopicsForFile,
} from "../WorkspaceCtx/hooks";

import { Header } from "./Header";
import styles from "./TopicsList.module.css";

export function TopicsList() {
  const { files: fileRecords } = useEphemeralWorkspaceState();
  const fileCount = fileRecords.length;
  // The only case that auto-selects a file is a single-file workspace.
  const autoSelectFileId = fileCount === 1 ? fileRecords[0].file_id : undefined;

  const { activeView, setActiveTab } = useWorkspaceSidebar();
  const dispatch = useVizDispatch();

  // A click in the Files tab writes initialSelectedFileId into sidebar state.
  // We read it eagerly so the first paint after a tab switch is already
  // correct (otherwise the "Select a file..." alert flashes briefly before
  // the effect below catches up).
  const initialSelectedFileId = isTabbedSidebarView(activeView)
    ? activeView.data?.initialSelectedFileId
    : undefined;

  const [selectedFileId, setSelectedFileId] = React.useState<
    string | undefined
  >(() => initialSelectedFileId?.fileId ?? autoSelectFileId);
  // Distinguishes a user click in the Files tab from the single-file
  // auto-select. Only manual selections should see the "Open in Document
  // Panel" alert for document-viewable files (the single-file auto-select
  // also shows it, handled separately below).
  const [wasManuallySelected, setWasManuallySelected] = React.useState(
    () => initialSelectedFileId?.fileId !== undefined,
  );

  // Handle the late-load single-file case: if fileRecords was empty at mount
  // but subsequently resolved to a single file, auto-select it.
  React.useEffect(() => {
    if (autoSelectFileId && selectedFileId === undefined) {
      setSelectedFileId(autoSelectFileId);
      setWasManuallySelected(false);
    }
  }, [autoSelectFileId, selectedFileId]);

  // Handle subsequent cross-tab clicks while TopicsList is already mounted,
  // and also re-fire when the user clicks the same file twice (key changes).
  React.useEffect(() => {
    if (initialSelectedFileId?.fileId) {
      setSelectedFileId(initialSelectedFileId.fileId);
      setWasManuallySelected(true);
    }
  }, [initialSelectedFileId?.fileId, initialSelectedFileId?.key]);

  const navigateToFilesTab = React.useCallback(() => {
    setActiveTab(SidebarTab.Files);
  }, [setActiveTab]);

  const selectedFile = fileRecords.find((f) => f.file_id === selectedFileId);
  const selectedFileTopics = useWorkspaceTopicsForFile(selectedFileId);
  const selectedFileName = selectedFile?.relative_path
    ? basename(selectedFile.relative_path)
    : undefined;
  const selectedFileIsDocumentViewable =
    selectedFileName !== undefined &&
    selectedFileTopics.length === 0 &&
    isDocumentViewable(selectedFileName);

  const openInDocumentPanel = React.useCallback(() => {
    if (!selectedFile?.relative_path) {
      return;
    }
    dispatch(
      actions.createDocumentPanelWithFile({
        fileId: selectedFile.file_id,
        fileName: selectedFile.relative_path,
      }),
    );
  }, [dispatch, selectedFile?.file_id, selectedFile?.relative_path]);

  if (!selectedFileId) {
    return (
      <div className={styles["no-file-message"]}>
        <Alert severity="info">
          Select a file from the{" "}
          <Link
            component="button"
            onClick={navigateToFilesTab}
            underline="hover"
          >
            Files tab
          </Link>{" "}
          to view its topics.
        </Alert>
      </div>
    );
  }

  // Show the document-panel prompt for manually-selected files, and also
  // for the single-file auto-select case — otherwise a single doc-viewable
  // file would land on an empty Topics view with no path forward.
  const showDocumentPanelPrompt =
    selectedFileIsDocumentViewable && (wasManuallySelected || fileCount === 1);

  if (showDocumentPanelPrompt) {
    return (
      <div className={styles["no-file-message"]}>
        <Alert severity="info">
          This file has no topics.{" "}
          <Link
            component="button"
            onClick={openInDocumentPanel}
            underline="hover"
          >
            Open in Document Panel
          </Link>{" "}
          to view its contents.
        </Alert>
      </div>
    );
  }

  return (
    <Topics
      fileId={selectedFileId}
      className={styles.topics}
      header={
        fileCount > 1 ? (
          <Header className={styles.header} onBack={navigateToFilesTab} />
        ) : undefined
      }
    />
  );
}
